/* ============================================================
   234LABS — product.js
   Variant resolution, price/availability, media sync, sticky ATC.
   Loaded only on product templates.
   ============================================================ */
/* Do NOT `import` from theme.js here.
   theme.liquid loads theme.js via {{ 'theme.js' | asset_url }} (versioned URL);
   a bare `import './theme.js'` resolves to a *different* URL, so the module
   would be evaluated a second time and every global listener (the quantity
   stepper especially) would bind twice → +2 per click. Read the helper off
   the window instead — same single instance the page already booted. */
const formatMoney = (cents) =>
  window.__234 && typeof window.__234.formatMoney === 'function'
    ? window.__234.formatMoney(cents)
    : '$' + (Number(cents) / 100).toFixed(2);

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[action*="/cart/add"]');
    this.idInput = this.querySelector('[name="id"]');
    this.variants = JSON.parse(this.querySelector('[data-variant-json]')?.textContent || '[]');
    this.priceTargets = document.querySelectorAll('[data-product-price]');
    this.submit = this.querySelectorAll('[data-add-button]');
    this.isPreorder = this.dataset.preorder === 'true';
    this.section = this.closest('[data-product-section]') || document;
    this.onChange = this.onChange.bind(this);
    this.addEventListener('change', this.onChange);
    this.updateFromInputs({ pushState: false });
  }

  get selectedOptions() {
    // radio groups OR selects, in option order
    const groups = [...this.querySelectorAll('[data-option-index]')];
    const byIndex = {};
    groups.forEach((el) => {
      if (el.type === 'radio') {
        if (el.checked) byIndex[el.dataset.optionIndex] = el.value;
      } else {
        byIndex[el.dataset.optionIndex] = el.value;
      }
    });
    return Object.keys(byIndex)
      .sort((a, b) => a - b)
      .map((k) => byIndex[k]);
  }

  matchVariant(opts) {
    return (
      this.variants.find((v) => v.options.every((o, i) => o === opts[i])) || null
    );
  }

  onChange() {
    this.updateFromInputs({ pushState: true });
  }

  updateFromInputs({ pushState }) {
    const groups = this.querySelectorAll('[data-option-index]');
    const opts = this.selectedOptions;
    const variant = groups.length ? this.matchVariant(opts) : this.variants[0] || null;
    this.currentVariant = variant;

    // reflect availability on each radio label
    this.querySelectorAll('[data-option-index][type="radio"]').forEach((radio) => {
      const test = [...opts];
      test[Number(radio.dataset.optionIndex)] = radio.value;
      const match = this.matchVariant(test);
      const label = this.querySelector(`label[for="${radio.id}"]`);
      if (label) label.classList.toggle('is-unavailable', !match || !match.available);
    });

    if (!variant) {
      this.setButton('unavailable');
      return;
    }

    if (this.idInput) this.idInput.value = variant.id;

    if (this.isPreorder) {
      // Price and button stay whatever the server rendered — pre-order
      // items are locked regardless of actual variant stock.
      this.setButton('preorder');
    } else {
      // price
      this.priceTargets.forEach((el) => {
        const bfPct = Number(el.dataset.bfPct || 0);
        if (bfPct) {
          // Black Friday: the discount applies at checkout, so show the normal price struck through
          const sale = Math.floor((variant.price * (100 - bfPct)) / 100);
          el.innerHTML = `<s class="price__was">${formatMoney(variant.price)}</s> <span class="price__sale">${formatMoney(sale)}</span>`;
          el.classList.add('price--on-sale');
          return;
        }
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          el.innerHTML = `<s class="price__was">${formatMoney(variant.compare_at_price)}</s> <span class="price__sale">${formatMoney(
            variant.price
          )}</span>`;
          el.classList.add('price--on-sale');
        } else {
          el.textContent = formatMoney(variant.price);
          el.classList.remove('price--on-sale');
        }
      });

      document.querySelectorAll('[data-bf-save]').forEach((el) => {
        const pct = Number(el.dataset.bfPct || 0);
        const sale = Math.floor((variant.price * (100 - pct)) / 100);
        el.textContent = `You save ${formatMoney(variant.price - sale)}`;
      });

      this.setButton(variant.available ? 'available' : 'sold_out');
    }

    if (pushState) {
      this.syncMedia(variant);
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
      document.dispatchEvent(new CustomEvent('variant:change', { detail: variant }));
    }
  }

  setButton(state) {
    const labels = {
      available: this.dataset.labelAdd || 'Add to cart',
      sold_out: this.dataset.labelSoldOut || 'Sold out',
      unavailable: this.dataset.labelUnavailable || 'Unavailable',
      preorder: this.dataset.labelPreorder || 'Pre-order soon',
    };
    this.submit.forEach((btn) => {
      btn.disabled = state !== 'available';
      btn.toggleAttribute('aria-disabled', state !== 'available');
      const span = btn.querySelector('[data-add-label]') || btn;
      span.textContent = labels[state];
    });
  }

  syncMedia(variant) {
    const mediaId = variant.featured_media?.id;
    if (!mediaId) return;
    const target = this.section.querySelector(`[data-media-id="${mediaId}"]`);
    if (!target) return;
    // stacked gallery: bring the variant image to the top visually via order
    const gallery = target.closest('[data-gallery]');
    if (gallery) {
      gallery.prepend(target);
      if (!this.dataset.userScrolled) target.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    }
  }
}
if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

/* ---------- bundle builder ("Build your set") ----------
   Adds the chosen colours to the cart in one request. The set price itself
   comes from the automatic discounts in Shopify admin, which fire on cart
   quantity, so this only has to put the right pieces in the cart. */
class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.prices = {
      1: Number(this.getAttribute('data-price-1')),
      2: Number(this.getAttribute('data-price-2')),
      3: Number(this.getAttribute('data-price-3')),
    };
    this.items = [...this.querySelectorAll('[data-bundle-item]')];
    this.total = this.querySelector('[data-bundle-total]');
    this.was = this.querySelector('[data-bundle-was]');
    this.saving = this.querySelector('[data-bundle-saving]');
    this.addBtn = this.querySelector('[data-bundle-add]');
    this.addLabel = this.querySelector('[data-bundle-add-label]');
    this.hint = this.querySelector('[data-bundle-hint]');

    // keep these events out of the product form's own variant logic
    this.addEventListener('change', (e) => {
      e.stopPropagation();
      const item = e.target.closest('[data-bundle-item]');
      if (item && e.target.matches('[data-bundle-check]')) {
        item.querySelector('[data-bundle-size]').disabled = !e.target.checked;
      }
      this.render();
    });
    this.addBtn.addEventListener('click', () => this.add());

    // mirror the main size picker into this page's own colour
    this.onVariant = (e) => {
      const sel = this.querySelector('.is-current [data-bundle-size]');
      const id = String(e.detail?.id || '');
      if (sel && [...sel.options].some((o) => o.value === id)) {
        sel.value = id;
        this.render();
      }
    };
    document.addEventListener('variant:change', this.onVariant);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.onVariant);
  }

  get picked() {
    return this.items
      .filter((li) => li.querySelector('[data-bundle-check]').checked)
      .map((li) => ({ li, variantId: li.querySelector('[data-bundle-size]').value }));
  }

  render() {
    const picked = this.picked;
    const n = Math.min(picked.length, 3);
    const missingSize = picked.some((p) => !p.variantId);

    this.querySelectorAll('[data-bundle-tier]').forEach((t) => {
      t.classList.toggle('is-active', Number(t.dataset.bundleTier) === n);
    });

    const regular = this.prices[1] * n;
    const total = this.prices[n];
    this.total.textContent = formatMoney(total);
    const saved = regular - total;
    this.was.hidden = saved <= 0;
    this.was.textContent = saved > 0 ? formatMoney(regular) : '';
    this.saving.textContent = saved > 0 ? `You save ${formatMoney(saved)}` : '';

    let hint = '';
    let ready = false;
    if (n < 2) {
      hint = `Tick one more colour to get two for ${formatMoney(this.prices[2])}.`;
    } else if (missingSize) {
      hint = 'Choose a size for each colour.';
    } else {
      ready = true;
      hint = n === 2 ? `Add a third colour and pay ${formatMoney(this.prices[3])} for all three.` : '';
    }
    this.hint.textContent = hint;
    this.addBtn.disabled = !ready;
    this.addBtn.toggleAttribute('aria-disabled', !ready);
    this.addLabel.textContent = n >= 2 ? `Add ${n} colours to cart` : 'Add the set to cart';
  }

  async add() {
    const items = this.picked.filter((p) => p.variantId).map((p) => ({ id: Number(p.variantId), quantity: 1 }));
    if (items.length < 2) return;
    this.addBtn.disabled = true;
    this.addBtn.classList.add('is-loading');
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      if (!res.ok) {
        window.__cart?.toast(data.description || data.message || 'Could not add the set', 'error');
        return;
      }
      window.__cart?.toast('Set added to cart');
      if (window.__cart) await window.__cart.refresh({ open: true });
      else location.href = `${root}cart`;
    } catch (_) {
      window.__cart?.toast('Network error, try again', 'error');
    } finally {
      this.addBtn.classList.remove('is-loading');
      this.render();
    }
  }
}
if (!customElements.get('bundle-builder')) customElements.define('bundle-builder', BundleBuilder);

/* ---------- sticky add-to-cart (mobile) ---------- */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.anchor = document.querySelector('[data-atc-anchor]');
    if (!this.anchor) return;
    this.io = new IntersectionObserver(
      ([entry]) => this.toggleAttribute('data-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 0 }
    );
    this.io.observe(this.anchor);
    // proxy submit to the real form
    this.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
      document.querySelector('[data-product-form] form[action*="/cart/add"] [data-add-button]')?.click();
    });
  }
  disconnectedCallback() {
    this.io?.disconnect();
  }
}
if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);

/* ---------- related products (async) ---------- */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url) return;
    fetch(url)
      .then((r) => r.text())
      .then((text) => {
        const fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim().length) this.innerHTML = fresh.innerHTML;
        else this.hidden = true;
      })
      .catch(() => (this.hidden = true));
  }
}
if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

/* ---------- share ---------- */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-share]');
  if (!btn) return;
  const url = btn.dataset.shareUrl || location.href;
  if (navigator.share) {
    try {
      await navigator.share({ url, title: document.title });
    } catch (_) {}
  } else {
    await navigator.clipboard?.writeText(url);
    const old = btn.textContent;
    btn.textContent = 'Copied';
    setTimeout(() => (btn.textContent = old), 1500);
  }
});

/* ---------- gallery carousel (one image per view + dots + arrows) ---------- */
let lastGalleryScroll = 0;
function initGallery() {
  const track = document.querySelector('.pdp-gallery');
  if (!track || track.dataset.carousel) return;
  const slides = [...track.querySelectorAll('.pdp-media')];
  if (slides.length < 2) return;
  track.dataset.carousel = '1';

  const media = track.closest('.pdp__media') || track.parentElement;

  const idx = () => {
    const t = track.getBoundingClientRect().left;
    let best = 0,
      bd = Infinity;
    slides.forEach((s, i) => {
      const d = Math.abs(s.getBoundingClientRect().left - t);
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    return best;
  };
  const goto = (i) => {
    const n = Math.max(0, Math.min(slides.length - 1, i));
    track.scrollTo({ left: slides[n].offsetLeft, behavior: 'smooth' });
  };

  const dots = document.createElement('div');
  dots.className = 'pdp-dots';
  slides.forEach((_, i) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'pdp-dot';
    b.setAttribute('aria-label', `Image ${i + 1}`);
    b.addEventListener('click', () => goto(i));
    dots.appendChild(b);
  });
  track.after(dots);

  const arrow = (dir) => {
    const a = document.createElement('button');
    a.type = 'button';
    a.className = `pdp-arrow pdp-arrow--${dir}`;
    a.setAttribute('aria-label', dir === 'prev' ? 'Previous image' : 'Next image');
    a.textContent = dir === 'prev' ? '‹' : '›';
    a.addEventListener('click', () => goto(idx() + (dir === 'prev' ? -1 : 1)));
    return a;
  };
  const prev = arrow('prev');
  const next = arrow('next');
  media.append(prev, next);

  const sync = () => {
    const c = idx();
    [...dots.children].forEach((d, i) => d.classList.toggle('is-active', i === c));
    prev.disabled = c === 0;
    next.disabled = c === slides.length - 1;
  };
  track.addEventListener(
    'scroll',
    () => {
      lastGalleryScroll = Date.now();
      requestAnimationFrame(sync);
    },
    { passive: true }
  );
  window.addEventListener('resize', sync);
  sync();
}
initGallery();

/* ---------- size guide dialog ---------- */
document.addEventListener('click', (e) => {
  if (e.target.closest('[data-size-guide-open]')) {
    e.preventDefault();
    const dlg = document.querySelector('[data-size-guide]');
    if (dlg && typeof dlg.showModal === 'function') dlg.showModal();
    return;
  }
  const openDlg = document.querySelector('[data-size-guide][open]');
  if (openDlg && (e.target.closest('[data-size-guide-close]') || e.target === openDlg)) {
    openDlg.close();
  }
});

/* ---------- lightweight image zoom ---------- */
document.addEventListener('click', (e) => {
  const img = e.target.closest('[data-zoomable]');
  if (!img) return;
  if (Date.now() - lastGalleryScroll < 350) return; // ignore taps right after a swipe
  const dlg = document.createElement('dialog');
  dlg.className = 'media-zoom';
  dlg.innerHTML = `<img src="${img.dataset.zoomSrc || img.currentSrc || img.src}" alt="">`;
  dlg.addEventListener('click', () => dlg.close());
  dlg.addEventListener('close', () => dlg.remove());
  document.body.appendChild(dlg);
  dlg.showModal();
});
